import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaPlus as FaPlusSolid } from 'react-icons/fa';
import { useSelector } from 'react-redux';

import { useQueryClient } from '@tanstack/react-query';
import { Popover, Spin } from 'antd';
import AddNewButton from 'components/common/Button/AddNewButton';
import Chip from 'components/common/Chip/Chip';
import MultiSelectHint from 'components/common/MultiSelectHint/MultiSelectHint';
import SearchInput from 'components/common/SearchInput/SearchInput';
import ShortcutHint from 'components/common/ShortcutHint/ShortcutHint';
import { M_SEARCH_CONSTRAINT_QUERY_KEY } from 'constants/queryKeys';
import {
  STEP_SELECTOR_FETCHING_LABEL,
  STEP_SELECTOR_SEARCH_PLACEHOLDER,
  TASKS_GRID_TARGET_ADD_PLACEHOLDER,
  TASKS_GRID_TARGET_FIELD_LABEL,
  TASKS_GRID_TARGET_SAVE_HINT,
} from 'constants/uiConstants';
import type { IItem } from 'interfaces/IInput';
import MergeClassNames from 'utils/MergeClassNames';

import { selectActiveGOrgId } from '../../../../redux/selectors/GlobalCatalogueSelectors';

import CatalogueOptionList from './CatalogueOptionList';
import { isModifierClick } from './pickerFlyoutRules';
import QuickAddConfirm from './QuickAddConfirm';
import SelectionActionBar from './SelectionActionBar';
import SelectionChips from './SelectionChips';
import {
  getStepOptionId,
  stepOptionsMatch,
  StepSelectorOption,
} from './stepConfig';
import { useConstraintCatalogue } from './useConstraintCatalogue';

interface TargetSpeciesPickerProps {
  value: IItem[];
  onCommit: (items: IItem[]) => void;
}

// Converts a StepSelectorOption back to IItem for persisting to the task.
const toIItem = (option: StepSelectorOption): IItem => ({
  item: option.item,
  globalId: option.globalId ?? null,
});

const toOption = (item: IItem): StepSelectorOption => ({
  label: item.item,
  item: item.item,
  globalId: item.globalId ?? null,
});

const targetKey = (item: IItem, index: number) =>
  item.globalId ?? `${item.item}:${index}`;

// The same small outlined "+" button as "+ Add input".
const ADD_BUTTON_CLASS =
  'gap-1 border border-solid border-gray-argent bg-white px-2 py-1 text-xs font-semibold text-secondary-dark hover:border-gray-argent hover:bg-gray-bright';

// Target, reason or pest for an application (OP-2534, reworked in OP-2556).
// Every target shows as its own tag, removed with its ×, then "+ Target",
// which only ever adds — and adds the way the product picker does: a plain
// click adds that target and closes; Ctrl/⌘+click starts a multi-select
// (ticks, chips, Cancel / Save) that adds the ticked set. Targets already on
// the task aren't listed. Keys as in the product picker: ↑/↓ to move, Enter
// to add (or tick, mid multi-select, as does Space outside the search),
// Ctrl/⌘+Enter or Shift+Enter to save a multi-select, Esc to cancel.
const TargetSpeciesPicker: React.FC<TargetSpeciesPickerProps> = ({
  value,
  onCommit,
}) => {
  const gOrgId = useSelector(selectActiveGOrgId);
  const queryClient = useQueryClient();

  const {
    constraintOptions,
    isLoading,
    isSearchFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    hasFetchNextPageError,
    searchTerm,
    onSearch,
    clearSearch,
  } = useConstraintCatalogue(gOrgId);

  const [isOpen, setIsOpen] = useState(false);
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [checkedItems, setCheckedItems] = useState<StepSelectorOption[]>([]);
  const [highlightedItem, setHighlightedItem] =
    useState<StepSelectorOption | null>(null);

  // Purge MSearch cache when this picker unmounts so a fresh re-mount
  // always starts with current data (mirrors StepInputSelector's own cleanup).
  useEffect(() => {
    return () => {
      queryClient.removeQueries({
        queryKey: [M_SEARCH_CONSTRAINT_QUERY_KEY],
      });
    };
  }, [queryClient]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        setIsMultiSelectMode(false);
        setCheckedItems([]);
        setHighlightedItem(null);
        clearSearch();
      }
      setIsOpen(open);
    },
    [clearSearch]
  );

  // The task's targets aren't offered again.
  const options = useMemo(() => {
    const current = value.map(toOption);
    return constraintOptions.filter(
      (option) =>
        !current.some((existing) => stepOptionsMatch(existing, option))
    );
  }, [constraintOptions, value]);

  const checkedGlobalIds = useMemo(
    () =>
      new Set(
        checkedItems.map((i) => i.globalId).filter((id): id is string => !!id)
      ),
    [checkedItems]
  );

  const addTargets = useCallback(
    (items: StepSelectorOption[]) => {
      if (items.length > 0) onCommit([...value, ...items.map(toIItem)]);
      setIsOpen(false);
    },
    [onCommit, value]
  );

  const toggleCheckedItem = useCallback((item: StepSelectorOption) => {
    setHighlightedItem(item);
    setCheckedItems((prev) => {
      const exists = prev.some((existing) => stepOptionsMatch(existing, item));
      return exists
        ? prev.filter((existing) => !stepOptionsMatch(existing, item))
        : [...prev, item];
    });
  }, []);

  const handleOptionClick = (
    item: StepSelectorOption,
    event: React.MouseEvent
  ) => {
    if (isMultiSelectMode || isModifierClick(event)) {
      setIsMultiSelectMode(true);
      toggleCheckedItem(item);
      return;
    }
    addTargets([item]);
  };

  // A name already on the task isn't offered again as a new target.
  const isOnTask = (text: string) =>
    value.some((item) => item.item.toLowerCase() === text.trim().toLowerCase());

  const handleSave = () => addTargets(checkedItems);
  const handleCancel = () => setIsOpen(false);

  const moveHighlight = (step: 1 | -1) => {
    if (options.length === 0) return;
    const index = highlightedItem
      ? options.findIndex((option) => stepOptionsMatch(option, highlightedItem))
      : -1;
    const next = Math.min(Math.max(index + step, 0), options.length - 1);
    setHighlightedItem(options[next]);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const isInSearch = event.target instanceof HTMLInputElement;
    const isSaveKey =
      event.key === 'Enter' &&
      (event.ctrlKey || event.metaKey || event.shiftKey);
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      handleCancel();
    } else if (isSaveKey && isMultiSelectMode) {
      event.preventDefault();
      handleSave();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      moveHighlight(event.key === 'ArrowDown' ? 1 : -1);
    } else if (
      highlightedItem &&
      (event.key === 'Enter' ||
        (event.key === ' ' && !isInSearch && isMultiSelectMode))
    ) {
      event.preventDefault();
      if (isMultiSelectMode) {
        toggleCheckedItem(highlightedItem);
      } else {
        addTargets([highlightedItem]);
      }
    }
  };

  const removeTarget = (index: number) =>
    onCommit(value.filter((_, itemIndex) => itemIndex !== index));

  const overlayContent = (
    <div
      role="dialog"
      aria-label={TASKS_GRID_TARGET_FIELD_LABEL}
      className="flex h-[min(28rem,calc(100vh-13rem))] w-96 flex-col bg-white p-3"
      onKeyDown={handleKeyDown}
    >
      <SearchInput
        value={searchTerm}
        onChange={onSearch}
        placeholder={STEP_SELECTOR_SEARCH_PLACEHOLDER}
      />

      {/* As in the product picker: the hint until a multi-select starts,
          then the ticked targets as chips, in the same slot. */}
      <div className="mt-2 grid">
        <MultiSelectHint
          className={MergeClassNames(
            'col-start-1 row-start-1 w-full',
            isMultiSelectMode && 'invisible'
          )}
        />
        {isMultiSelectMode && checkedItems.length > 0 && (
          <div className="col-start-1 row-start-1 min-w-0 self-center">
            <SelectionChips
              items={checkedItems}
              onRemoveItem={(id) =>
                setCheckedItems((prev) =>
                  prev.filter((item) => getStepOptionId(item) !== id)
                )
              }
              singleLine
            />
          </div>
        )}
      </div>

      {isSearchFetching && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-gray-muted">
          <Spin size="small" />
          <span>{STEP_SELECTOR_FETCHING_LABEL}</span>
        </div>
      )}

      <div
        className={MergeClassNames(
          'mt-3 flex flex-1 flex-col overflow-hidden border border-gray-bright bg-white',
          isLoading && 'items-center justify-center'
        )}
      >
        {isLoading ? (
          <Spin size="small" />
        ) : (
          <CatalogueOptionList
            key="constraint"
            items={options}
            selectedGlobalId={highlightedItem?.globalId ?? null}
            onSelectItem={handleOptionClick}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            isFetchingNextPage={isFetchingNextPage}
            hasFetchNextPageError={hasFetchNextPageError}
            isMultiSelectMode={isMultiSelectMode}
            checkedGlobalIds={checkedGlobalIds}
            compact
            emptyAction={
              searchTerm.trim() &&
              !isSearchFetching &&
              !isOnTask(searchTerm) ? (
                <QuickAddConfirm
                  kind="target"
                  searchText={searchTerm}
                  onCreated={(record) =>
                    addTargets([
                      {
                        label: record.name,
                        item: record.name,
                        globalId: record.globalId,
                      },
                    ])
                  }
                />
              ) : undefined
            }
          />
        )}
      </div>

      {isMultiSelectMode && (
        <div className="flex items-end justify-between gap-2">
          <span className="flex items-center gap-1 pb-1.5 text-xs text-gray-muted">
            <ShortcutHint shortcutKey="Enter" variant="subtle" />
            {TASKS_GRID_TARGET_SAVE_HINT}
          </span>
          <SelectionActionBar
            isSaveDisabled={checkedItems.length === 0}
            onCancel={handleCancel}
            onSave={handleSave}
          />
        </div>
      )}
    </div>
  );

  return (
    <div
      role="group"
      aria-label={TASKS_GRID_TARGET_FIELD_LABEL}
      className="flex min-w-0 flex-wrap items-center gap-1"
    >
      {value.map((item, index) => (
        <Chip
          key={targetKey(item, index)}
          id={targetKey(item, index)}
          label={item.item}
          variant="filled"
          // Within the cell's width, so a long name truncates.
          className="max-w-full"
          truncate
          onClose={() => removeTarget(index)}
        />
      ))}
      <Popover
        open={isOpen}
        onOpenChange={handleOpenChange}
        content={overlayContent}
        trigger="click"
        placement="bottomLeft"
        destroyOnHidden
        classNames={{ root: 'target-species-picker-popover' }}
      >
        <span>
          <AddNewButton
            text={TASKS_GRID_TARGET_FIELD_LABEL}
            aria-label={TASKS_GRID_TARGET_ADD_PLACEHOLDER}
            size="sm"
            className={ADD_BUTTON_CLASS}
            icon={<FaPlusSolid className="text-secondary-dark" size={10} />}
          />
        </span>
      </Popover>
    </div>
  );
};

export default TargetSpeciesPicker;
